import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { MapPin } from "lucide-react";
import "leaflet/dist/leaflet.css";

// Fix default Leaflet icon (idempotent)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Centro mappa: città italiane principali (Firenze default)
const CITY_DEFAULTS = {
  Firenze: [43.7696, 11.2558],
  Prato: [43.8810, 11.0964],
  Pistoia: [43.9303, 10.9170],
  "Bagno a Ripoli": [43.7541, 11.3144],
  Calenzano: [43.8590, 11.1670],
  "Sesto Fiorentino": [43.8346, 11.1990],
  "Campi Bisenzio": [43.8181, 11.1407],
  Abetone: [44.1411, 10.6655],
};

function ClickHandler({ onPick }) {
  useMapEvents({
    click: (e) => onPick(e.latlng.lat, e.latlng.lng),
  });
  return null;
}

/**
 * Dialog fallback geocoding (BUG #1): si apre quando Nominatim non trova un indirizzo.
 * Mostra una mini-mappa Leaflet, pin draggable, 2 input lat/lng e un bottone Conferma.
 */
export function GeocodingFallbackDialog({
  open,
  citta,
  initialLat = null,
  initialLng = null,
  onCancel,
  onConfirm, // (lat, lng) => Promise
}) {
  const fallbackCenter = useMemo(() => {
    if (initialLat != null && initialLng != null) return [initialLat, initialLng];
    const c = CITY_DEFAULTS[citta] || CITY_DEFAULTS.Firenze;
    return c;
  }, [citta, initialLat, initialLng]);

  const [lat, setLat] = useState(fallbackCenter[0]);
  const [lng, setLng] = useState(fallbackCenter[1]);
  const [submitting, setSubmitting] = useState(false);
  const markerRef = useRef(null);

  useEffect(() => {
    setLat(fallbackCenter[0]);
    setLng(fallbackCenter[1]);
  }, [fallbackCenter]);

  const onPick = (la, ln) => {
    setLat(Number(la.toFixed(6)));
    setLng(Number(ln.toFixed(6)));
  };

  const onMarkerDragEnd = () => {
    const m = markerRef.current;
    if (!m) return;
    const ll = m.getLatLng();
    onPick(ll.lat, ll.lng);
  };

  const onSubmit = async () => {
    if (Number.isNaN(Number(lat)) || Number.isNaN(Number(lng))) return;
    setSubmitting(true);
    try {
      await onConfirm(Number(lat), Number(lng));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <DialogContent className="max-w-xl" data-testid="geocoding-fallback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin size={18} className="text-[#F57C00]" />
            Coordinate non trovate
          </DialogTitle>
          <DialogDescription>
            Non è stato possibile trovare automaticamente le coordinate dell'indirizzo.
            Posiziona il pin sulla mappa (clic o trascina) o inserisci le coordinate manualmente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="rounded-lg overflow-hidden border border-slate-200" style={{ height: 320 }}>
            <MapContainer
              center={fallbackCenter}
              zoom={14}
              style={{ height: "100%", width: "100%" }}
              data-testid="geocoding-fallback-map"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ClickHandler onPick={onPick} />
              <Marker
                position={[lat, lng]}
                draggable
                eventHandlers={{ dragend: onMarkerDragEnd }}
                ref={markerRef}
              />
            </MapContainer>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Latitudine</Label>
              <Input
                type="number"
                step="0.000001"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
                data-testid="geocoding-fallback-lat"
              />
            </div>
            <div>
              <Label>Longitudine</Label>
              <Input
                type="number"
                step="0.000001"
                value={lng}
                onChange={(e) => setLng(e.target.value)}
                data-testid="geocoding-fallback-lng"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            Annulla
          </Button>
          <Button
            onClick={onSubmit}
            disabled={submitting}
            className="bg-[#1B5E20] hover:bg-[#144818] text-white"
            data-testid="geocoding-fallback-confirm"
          >
            {submitting ? "Salvataggio..." : "Conferma e salva"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
